import React, { useState, useCallback } from "react";
import { tWithKeyAttr as t } from "../../i18nHelper";
import styles from "./exportButtons.module.css";
import { openAuthenticatedFile, downloadAuthenticatedFile } from "../../utils/fileDownload";
import {
  useLazyExportQuestionnaireToPdfQuery,
  useLazyExportQuestionnaireToExcelQuery,
  useLazyExportTherapistActionToPdfQuery,
  useLazyExportTherapistActionToExcelQuery,
} from "../../services/api/user.api";
import i18n from "../../i18n";
import { useAppSelector } from "../../services/hooks";
import { adminSelector } from "../../services/selectors/user.selectors";

interface ExportButtonsProps {
  reportType: "questionnaire" | "therapist-action";
  userId: number;
  questionnaireId: number;
  questionnaireCompaniesId: number;
  cycle: number;
  therapistId?: number;
  language?: string;
  disabled?: boolean;
  size?: "small" | "medium";
}

function ExportButtons({
  reportType,
  userId,
  questionnaireId,
  questionnaireCompaniesId,
  cycle,
  therapistId,
  language,
  disabled = false,
  size = "medium",
}: ExportButtonsProps) {
  // ========== STATE ==========
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ========== HOOKS ==========
  const currentLanguage = language || i18n.language || "he";
  const admin = useAppSelector(adminSelector);

  // Use refs to always have the latest values
  const paramsRef = React.useRef({
    userId,
    questionnaireId,
    questionnaireCompaniesId,
    cycle,
    therapistId,
    reportType,
  });

  // Update refs whenever props change
  React.useEffect(() => {
    paramsRef.current = {
      userId,
      questionnaireId,
      questionnaireCompaniesId,
      cycle,
      therapistId,
      reportType,
    };
  }, [
    userId,
    questionnaireId,
    questionnaireCompaniesId,
    cycle,
    therapistId,
    reportType,
  ]);

  // ========== DATA FETCHING ==========
  const [exportQuestionnaireToPdf] = useLazyExportQuestionnaireToPdfQuery();
  const [exportQuestionnaireToExcel] = useLazyExportQuestionnaireToExcelQuery();
  const [exportTherapistActionToPdf] = useLazyExportTherapistActionToPdfQuery();
  const [exportTherapistActionToExcel] =
    useLazyExportTherapistActionToExcelQuery();

  // ========== EVENT HANDLERS ==========
  const handleExportPdf = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      setExportingPdf(true);
      setError(null);

      try {
        let result;
        const params = paramsRef.current;

        if (params.reportType === "questionnaire") {
          result = await exportQuestionnaireToPdf({
            userId: params.userId,
            questionnaireCompaniesId: params.questionnaireCompaniesId,
            questionnaireId: params.questionnaireId,
            cycle: params.cycle,
            language: currentLanguage,
            userRole: (admin as any)?.roleId,
          });
        } else {
          result = await exportTherapistActionToPdf({
            appointmentId: 0,
            userId: params.userId,
            therapistId: params.therapistId || 0,
            cycle: params.cycle,
            questionnaireId: params.questionnaireId,
            questionnaireCompaniesId: params.questionnaireCompaniesId,
            language: currentLanguage,
            userRole: (admin as any)?.roleId,
          });
        }

        if ("data" in result && result.data) {
          await openAuthenticatedFile(result.data.fileUrl);
        } else if ("error" in result) {
          const errorStatus = (result.error as any)?.status;
          if (errorStatus === 401) {
            setError(t("EXPORT_ERROR_UNAUTHORIZED"));
          } else if (errorStatus === 404) {
            setError(t("EXPORT_ERROR_NOT_FOUND"));
          } else {
            setError(t("EXPORT_ERROR"));
          }
        }
      } catch (err) {
        // console.error("Export PDF error:", err);
        setError(t("EXPORT_ERROR"));
      } finally {
        setExportingPdf(false);
      }
    },
    [
      currentLanguage,
      admin,
      exportQuestionnaireToPdf,
      exportTherapistActionToPdf,
    ]
  );

  const handleExportExcel = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      setExportingExcel(true);
      setError(null);

      try {
        let result;
        const params = paramsRef.current;

        if (params.reportType === "questionnaire") {
          result = await exportQuestionnaireToExcel({
            userId: params.userId,
            questionnaireCompaniesId: params.questionnaireCompaniesId,
            questionnaireId: params.questionnaireId,
            cycle: params.cycle,
            language: currentLanguage,
            userRole: (admin as any)?.roleId,
          });
        } else {
          result = await exportTherapistActionToExcel({
            appointmentId: 0,
            userId: params.userId,
            therapistId: params.therapistId || 0,
            cycle: params.cycle,
            questionnaireId: params.questionnaireId,
            questionnaireCompaniesId: params.questionnaireCompaniesId,
            language: currentLanguage,
            userRole: (admin as any)?.roleId,
          });
        }

        if ("data" in result && result.data) {
          if (result.data.fileType === "excel") {
            const fileName = new URL(result.data.fileUrl, window.location.origin).pathname.split("/").pop() || "report.xlsx";
            await downloadAuthenticatedFile(result.data.fileUrl, fileName);
          } else {
            await openAuthenticatedFile(result.data.fileUrl);
          }
        } else if ("error" in result) {
          const errorStatus = (result.error as any)?.status;
          if (errorStatus === 401) {
            setError(t("EXPORT_ERROR_UNAUTHORIZED"));
          } else if (errorStatus === 404) {
            setError(t("EXPORT_ERROR_NOT_FOUND"));
          } else {
            setError(t("EXPORT_ERROR"));
          }
        }
      } catch (err) {
        // console.error("Export Excel error:", err);
        setError(t("EXPORT_ERROR"));
      } finally {
        setExportingExcel(false);
      }
    },
    [
      currentLanguage,
      admin,
      exportQuestionnaireToExcel,
      exportTherapistActionToExcel,
    ]
  );

  // ========== RENDER ==========
  const isLoading = exportingPdf || exportingExcel;
  const isDisabled = disabled || isLoading;

  return (
    <div className={styles.container}>
      <div className={styles.buttonsGroup}>
        <button
          type="button"
          className={`${styles.exportButton} ${styles.pdfButton} ${
            size === "small" ? styles.small : ""
          }`}
          onClick={handleExportPdf}
          disabled={isDisabled}
          title={t("EXPORT_PDF")}
        >
          {exportingPdf ? (
            <span className={styles.spinner}>⏳</span>
          ) : (
            <span className={styles.icon}>📄</span>
          )}
          {size === "medium" && (
            <span className={styles.label}>{t("EXPORT_PDF")}</span>
          )}
        </button>

        <button
          type="button"
          className={`${styles.exportButton} ${styles.excelButton} ${
            size === "small" ? styles.small : ""
          }`}
          onClick={handleExportExcel}
          disabled={isDisabled}
          title={t("EXPORT_EXCEL")}
        >
          {exportingExcel ? (
            <span className={styles.spinner}>⏳</span>
          ) : (
            <span className={styles.icon}>📊</span>
          )}
          {size === "medium" && (
            <span className={styles.label}>{t("EXPORT_EXCEL")}</span>
          )}
        </button>
      </div>

      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
}

export default ExportButtons;
